import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Space, Button, Typography, Input, message, Spin, Alert, Modal, Upload, Tooltip } from "antd";
import { isLogged, getAuthToken, destroyAuthToken } from "../../../helpers/account";
import { api } from "../../../config/api";
import { formatPolishDate } from "../../../helpers/date";
import {
    SendOutlined,
    PaperClipOutlined,
    RobotOutlined,
    ClockCircleOutlined
} from "@ant-design/icons";
import AvatarDisplay from "../../Dashboard/Account/AvatarDisplay";
import DOMPurify from 'dompurify';

const ChatWindow = (props) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [loadingAI, setLoadingAI] = useState(false);
    const [messageSent, setMessageSent] = useState(false);
    const [messages, setMessages] = useState([]);
    const [messageInput, setMessageInput] = useState('');
    const [uploading, setUploading] = useState(false);
    const [isScrolling, setIsScrolling] = useState(false);
    const { TextArea } = Input;
    const { Text } = Typography;
    const [attachmentFile, setAttachmentFile] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        if (!isScrolling && messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result.split(",")[1]);
            reader.onerror = (error) => reject(error);
        });
    };

    const handleUploadAttachment = async () => {
        try {
            if (!attachmentFile) {
                message.error("Nie wybrano pliku.");
                return;
            }

            setUploading(true);
            const base64File = await convertFileToBase64(attachmentFile);

            const response = await fetch(`${api.host}/attachment`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ attachmentFile: base64File }),
            });

            if (!response.ok) {
                throw new Error("Błąd przesyłania pliku.");
            }

            const data = await response.json();
            const uploadMessage = `Przesyłam plik: <a href="${data.publicUrl}" target="_blank">Pobierz</a>. <i>(Pliki ZIP mogą zawierać różne rodzaje danych, dlatego warto je sprawdzać zaufanym oprogramowaniem, aby upewnić się, że są bezpieczne.)</i>`;

            const apiUrl = `${api.host}/transactions/chats`;
            const sendResponse = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: getAuthToken(),
                },
                body: JSON.stringify({
                    uuid: props.transactionUuid,
                    message: uploadMessage,
                }),
            });

            if (sendResponse.ok) {
                message.success("Plik został przesłany i wiadomość została wysłana.");
                setMessageSent(true);
                scrollToBottom();
            } else {
                const errorData = await sendResponse.json();
                message.error(errorData.error || "Błąd podczas wysyłania wiadomości.");
            }
        } catch (error) {
            console.error("Error uploading attachment:", error);
            message.error("Błąd podczas przesyłania załącznika.");
        } finally {
            setUploading(false);
            setIsModalVisible(false);
        }
    };

    const handleFileChange = (info) => {
        const file = info.file.originFileObj || info.file;
        if (!file) {
            message.error("Nie udało się załadować pliku. Spróbuj ponownie.");
            return;
        }

        if (file.type !== "application/zip" && !file.name.endsWith(".zip")) {
            message.error("Możesz przesłać tylko pliki ZIP.");
            return;
        }

        setAttachmentFile(file);
        setIsModalVisible(true);
    };

    const handleCancelUpload = () => {
        setAttachmentFile(null);
        setIsModalVisible(false);
        message.info("Przesyłanie pliku zostało anulowane.");
    };

    const handleAssistantButtonClick = async () => {
        setMessageInput('@TaskizyBot ');
    };

    const handleSendMessage = async () => {
        if (!messageInput.trim()) {
            return;
        }

        setLoading(true);
        const apiUrl = `${api.host}/transactions/chats`;

        try {
            const mentionRegex = /@TaskizyBot/gi;
            if (mentionRegex.test(messageInput)) {
                setLoadingAI(true);
                const assistantResponse = await fetch(`${api.host}/chatbot/transaction-assistant`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: getAuthToken(),
                    },
                    body: JSON.stringify({
                        transactionUuid: props.transactionUuid,
                        message: messageInput.replace(mentionRegex, "").trim(),
                    }),
                });

                if (assistantResponse.ok) {
                    const assistantData = await assistantResponse.json();

                    const response = await fetch(apiUrl, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: getAuthToken(),
                        },
                        body: JSON.stringify({
                            uuid: props.transactionUuid,
                            message: assistantData,
                        }),
                    });

                    if (response.ok) {
                        message.success("Wirtualny asystent odpowiedział na Twoją wiadomość.");
                        setMessageInput('');
                        setMessageSent(true);
                        scrollToBottom();
                        setLoadingAI(false);
                    } else {
                        const errorData = await response.json();
                        message.error(errorData.error);
                        setLoadingAI(false);
                    }
                } else {
                    const errorData = await assistantResponse.json();
                    message.error(errorData.error || "Błąd podczas komunikacji z AI Assistant.");
                }
            } else {
                const response = await fetch(apiUrl, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: getAuthToken(),
                    },
                    body: JSON.stringify({
                        uuid: props.transactionUuid,
                        message: messageInput,
                    }),
                });

                if (response.ok) {
                    message.success("Wiadomość wysłana");
                    setMessageInput('');
                    setMessageSent(true);
                    scrollToBottom();
                } else {
                    const errorData = await response.json();
                    message.error(errorData.error);
                }
            }
        } catch (error) {
            console.error("Error sending message:", error);
            message.error("Wystąpił błąd podczas wysyłania wiadomości.");
        } finally {
            setLoading(false);
        }
    };

    const fetchMessages = async () => {
        if (props.transactionUuid || messageSent) {
            const apiUrl = `${api.host}/transactions/chats/${props.transactionUuid}`;

            try {
                const response = await fetch(apiUrl, {
                    method: "GET",
                    headers: new Headers({
                        "Content-Type": "application/json",
                        Authorization: `${getAuthToken()}`,
                    }),
                });

                if (response.ok) {
                    const data = await response.json();
                    setMessages(data);
                    setLoading(false);
                } else {
                    throw new Error('Failed to fetch messages');
                }
            } catch (error) {
                setTimeout(() => {
                    message.error("Dla bezpieczeństwa zostałeś wylogowany.");
                    destroyAuthToken();
                    window.location.href = "/transactions";
                }, 2000);
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchMessages();
        const intervalId = setInterval(fetchMessages, 5000);
        return () => clearInterval(intervalId);
    }, [props.transactionUuid, messageSent]);

    const handleWheel = () => {
        setIsScrolling(true);
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
            setIsScrolling(false);
        }, 1500);
    };

    let scrollTimeout;

    return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Spin spinning={loading || loadingAI}>
                {/* Messages Container */}
                <div
                    style={{
                        height: "300px",
                        overflowY: "auto",
                        padding: "16px",
                        background: "#f8f9fa",
                        borderRadius: "8px 8px 0 0",
                    }}
                    onWheel={handleWheel}
                >
                    {messages.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "40px 20px" }}>
                            <div style={{ fontSize: "48px", marginBottom: "16px", opacity: 0.5 }}>💬</div>
                            <Text style={{ color: "#6c757d" }}>
                                Brak wiadomości. Rozpocznij rozmowę!
                            </Text>
                        </div>
                    ) : (
                        <Space direction="vertical" size="small" style={{ width: "100%" }}>
                            {messages.map((msg, index) => {
                                const isMyMessage = sessionStorage.getItem("uuid") === msg.account.uuid;
                                return (
                                    <div
                                        key={index}
                                        style={{
                                            display: "flex",
                                            justifyContent: isMyMessage ? "flex-end" : "flex-start",
                                            marginBottom: "12px",
                                            alignItems: "flex-start",
                                            gap: "8px"
                                        }}
                                    >
                                        {!isMyMessage && (
                                            <AvatarDisplay src={msg.account.photo} size={32} />
                                        )}
                                        <div
                                            style={{
                                                maxWidth: "70%",
                                                padding: "12px 16px",
                                                borderRadius: "18px",
                                                background: isMyMessage ? "#667eea" : "#ffffff",
                                                color: isMyMessage ? "#ffffff" : "#2c3e50",
                                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                                position: "relative",
                                            }}
                                        >
                                            {!isMyMessage && (
                                                <div style={{ 
                                                    fontSize: "12px",
                                                    opacity: 0.8,
                                                    marginBottom: "4px",
                                                    fontWeight: "500"
                                                }}>
                                                    <Text style={{ color: "#6c757d", fontSize: "12px" }}>
                                                        {msg.account.fullname}
                                                    </Text>
                                                </div>
                                            )}
                                            <div
                                                style={{ 
                                                    lineHeight: "1.4",
                                                    wordBreak: "break-word"
                                                }}
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(msg.message.replace(/\\n/g, '<br/>'), {
                                                        ALLOWED_TAGS: ['a', 'i', 'br'],
                                                        ALLOWED_ATTR: ['href', 'target', 'rel'],
                                                    })
                                                }}
                                            />
                                            <div style={{ 
                                                fontSize: "11px", 
                                                opacity: 0.7, 
                                                marginTop: "4px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                justifyContent: isMyMessage ? "flex-end" : "flex-start"
                                            }}>
                                                <ClockCircleOutlined />
                                                {formatPolishDate(msg.created_at)}
                                            </div>
                                        </div>
                                        {isMyMessage && (
                                            <AvatarDisplay src={msg.account.photo} size={32} />
                                        )}
                                    </div>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </Space>
                    )}
                </div>

                {/* Input Container */}
                <div
                    style={{
                        padding: "16px",
                        background: "#ffffff",
                        borderTop: "1px solid #e9ecef",
                        borderRadius: "0 0 8px 8px",
                    }}
                >
                    <Space.Compact style={{ width: "100%" }}>
                        <TextArea
                            placeholder="Wpisz wiadomość..."
                            autoSize={{ minRows: 1, maxRows: 3 }}
                            value={messageInput}
                            onChange={(e) => {
                                setMessageInput(e.target.value);
                                if (messageSent) { setMessageSent(false); }
                            }}
                            onPressEnter={(e) => {
                                if (!e.shiftKey) {
                                    e.preventDefault();
                                    handleSendMessage();
                                }
                            }}
                            style={{ 
                                borderRadius: "20px",
                                resize: "none",
                                border: "1px solid #d9d9d9"
                            }}
                        />
                    </Space.Compact>
                    
                    <div style={{ 
                        display: "flex", 
                        justifyContent: "space-between", 
                        alignItems: "center", 
                        marginTop: "12px" 
                    }}>
                        <Space>
                            <Upload
                                accept=".zip"
                                beforeUpload={() => false}
                                onChange={handleFileChange}
                                showUploadList={false}
                            >
                                <Tooltip title="Prześlij plik ZIP">
                                    <Button 
                                        type="text" 
                                        icon={<PaperClipOutlined />}
                                        style={{ color: "#6c757d" }}
                                    />
                                </Tooltip>
                            </Upload>
                            <Tooltip title="Podsumuj rozmowę oraz ustalenia z asystentem AI">
                                <Button 
                                    type="text" 
                                    icon={<RobotOutlined />}
                                    onClick={handleAssistantButtonClick}
                                    style={{ color: "#6c757d" }}
                                >
                                    AI
                                </Button>
                            </Tooltip>
                        </Space>
                        
                        <Button 
                            type="primary" 
                            icon={<SendOutlined />}
                            onClick={handleSendMessage}
                            disabled={!messageInput.trim()}
                            style={{ 
                                borderRadius: "20px",
                                background: "#667eea",
                                borderColor: "#667eea"
                            }}
                        >
                            Wyślij
                        </Button>
                    </div>
                </div>
            </Spin>

            <Modal
                title="Potwierdzenie przesłania pliku"
                open={isModalVisible}
                onOk={handleUploadAttachment}
                onCancel={handleCancelUpload}
                confirmLoading={uploading}
                okText="Prześlij"
                cancelText="Anuluj"
            >
                <p>Czy na pewno chcesz przesłać plik: <strong>{attachmentFile?.name}</strong>?</p>
            </Modal>
        </div>
    );
};

export default ChatWindow;